import type { Messages } from './en';

/**
 * 第四階段（天空）：天氣、日曆、颱風、地震和媽媽的來電。
 * 標示「英文練習」的句子在所有語言中都刻意保留英文。
 */
export const zhTWSky: Pick<Messages, 'sky'> = {
  sky: {
    mapNames: {},
    zones: {},
    objects: {
      sky_calendar: '日曆',
      sky_kit: '防災包',
      sky_umbrellas: '雨傘',
    },
    npc: {},
    items: {},

    kinds: {
      sunny: '晴天',
      cloudy: '多雲',
      rain: '下雨',
      storm: '雷陣雨',
      cool: '鋒面報到',
      typhoon: '颱風',
    },
    hud: {
      /** 時間框的提示文字。 */
      title: (week: number, date: string, kind: string) => `第 ${week} 週 · ${date} · ${kind}`,
    },

    forecast: {
      title: '天氣預報',
      today: '今天',
      tomorrow: '明天',
      rain: (p: number) => `☂ ${p}%`,
      /** 用今天真正的預報取代跑馬燈上固定的天氣句。 */
      tickerToday: (kind: string, lo: number, hi: number, p: number) => `天氣：${kind}，${lo}–${hi}°C，降雨機率 ${p}% · 提醒：分享前先查證來源 ·`,
      range: (lo: number, hi: number) => `${lo}–${hi}°C`,
      showers: (from: string, to: string) => `${from}–${to} 有陣雨`,
      alert: '🌀 颱風警報！明天放颱風假：停班停課，請待在室內！',
      ticker: '🌀 颱風警報 · 氣象署發布海上陸上颱風警報 · 明天停班停課 · 記得儲水、手機充飽電 ·',
      tips: {
        sunny: '又熱又晴：多喝水、記得戴帽子。',
        cloudy: '多雲，可能會有短暫陣雨。',
        rain: '下雨天：記得帶傘！',
        storm: '午後雷陣雨：帶把傘，也別待在高大的樹下。',
        cool: '鋒面來了，天氣變冷：記得穿外套。',
        typhoon: '颱風來了！待在家裡，遠離窗戶。',
      },
    },

    toasts: {
      typhoonEve: '🌀 颱風警報：明天放颱風假！先在家做好準備。',
      typhoonDay: '🌀 放颱風假！學校和店家都關了，乖乖待在室內。',
      typhoonPassed: '🌤️ 颱風走了，門又開了。',
      doorClosed: '颱風天暫停營業，請待在室內注意安全！',
      tooWindy: '風實在太大了，颱風天出門很危險！',
      rainStarts: '🌧️ 開始下雨了！',
      stormStarts: '⛈️ 打雷了！暴風雨要來了。',
      soaked: '💦 你被雨淋成落湯雞了！去超商買把傘吧。',
      umbrellaOpen: '☂️ 你撐開雨傘，乾乾爽爽！',
      sentHome: '🌀 颱風假：今天不用上課，你待在家裡。',
      windy: '🍃 外面的颱風呼呼地吹！',
      quakeAlert: '📳 地震速報！強烈搖晃即將來臨——快保護頭部！',
    },

    calendar: {
      who: '日曆',
      today: (date: string, kind: string, temp: number, pct: number) => `今天是 **${date}**。${kind}，${temp}°C，降雨機率 ${pct}%。`,
      tomorrow: (kind: string, lo: number, hi: number, pct: number) => `明天：${kind}，${lo}–${hi}°C，降雨機率 ${pct}%。`,
      tear: '你撕下昨天那一頁。很多台灣家庭都掛著這種撕的日曆。',
      practice: '用英文說說看',
      // 英文練習句。
      sayDate: (date: string) => `Today is ${date}.`,
      say: {
        sunny: 'It’s sunny and hot today.',
        cloudy: 'It’s cloudy today.',
        rain: 'It’s rainy today. I need an umbrella.',
        storm: 'There will be a thunderstorm this afternoon.',
        cool: 'It’s cold today. I need a jacket.',
        typhoon: 'A typhoon is coming. Stay at home!',
      },
    },

    kit: {
      title: '🌀 防颱檢查表',
      intro: '趁風雨變大之前把家裡準備好。每完成一項就點一下。',
      calm: '我們的防災包：水、手電筒、電池和急救箱。颱風、地震都不怕！',
      calmTomorrow: '颱風明天就來了。明天早上照著這張檢查表把家裡準備好。',
      done: '✅ 完成',
      go: '去做',
      goBalcony: '去陽台',
      all: '全部準備好了。做得好，防颱小達人！',
      items: {
        // 英文練習句（`en` 欄位）。
        water: { icon: '💧', label: '把水瓶和浴缸裝滿水', en: 'Fill the water bottles.' },
        phone: { icon: '🔋', label: '手機和行動電源充飽電', en: 'Charge the phone.' },
        plants: { icon: '🪴', label: '把陽台的盆栽搬進來', en: 'Bring the plants inside.' },
        flashlight: { icon: '🔦', label: '找出手電筒和電池', en: 'Where is the flashlight?' },
      },
      balconyAsk: '風好大！花盆可能會從陽台被吹走。要搬進來嗎？',
      balconyYes: '🪴 搬進來',
      balconyNo: '等一下',
      balconyDone: '花盆都安全地搬進屋裡了。',
    },

    typhoonMorning: {
      who: '媽媽',
      text: '早安！颱風來了，今天放颱風假：停班停課。你可以幫我把家裡準備好嗎？檢查表就在門邊的防災包上。',
      good: '「好啊，媽媽！我來裝水、幫手機充電。」',
      goodMsg: '媽媽笑了：「我的小幫手！」',
      bad: '「放假？耶，我要出去玩！」',
      badMsg: '媽媽皺起眉頭：「絕對不行！外面很危險。」',
    },

    quake: {
      who: '地震！',
      text: '地在搖！東西喀啦喀啦響，還掉下來了。你該怎麼做？',
      drop: '🫣 趴下、掩護、穩住',
      outside: '🏃 用最快的速度跑到外面',
      lift: '🛗 搭電梯下樓',
      window: '🪟 站在窗邊看',
      right: '答對了！趴下，躲到桌子底下，護住頭部，穩住直到搖晃停止。',
      wrongOutside: '不安全！搖晃時奔跑可能會跌倒，或被掉落的玻璃和招牌砸到。',
      wrongLift: '地震時千萬不要搭電梯：電梯可能會停住把你困在裡面。等搖晃停了再走樓梯。',
      wrongWindow: '窗戶可能會破！要遠離玻璃。',
      // 英文練習句。
      phrase: 'Drop, cover and hold on!',
      lesson: '用英文說說看：**“Drop, cover and hold on!”**（趴下、掩護、穩住！）台灣地震很多，所以大家在學校都會演練。',
      after: '搖晃停了。呼……大家都沒事。',
      momWho: '媽媽',
      momHome: '媽媽趕緊跑過來：「你還好嗎？有沒有東西砸到你？」',
      momGood: '「我沒事，媽媽！我有趴下、掩護、穩住。」',
      momGoodMsg: '媽媽緊緊抱住你。',
      momBad: '「沒什麼啦，別擔心了。」',
      momBadMsg: '媽媽嘆了口氣，還在發抖。',
    },

    umbrella: {
      useRain: '☂️ 你撐開雨傘，雨滴咚咚地打在傘上。',
      useDry: '現在沒下雨。你把傘收在包包裡。',
      who: '傘架',
      ask: (price: number) => `門口有一桶雨傘，就像台灣每家超商一樣。一把 ${price} 枚硬幣。`,
      buy: (price: number) => `☂️ 買一把（${price}🪙）`,
      no: '不用了，謝謝',
      poor: '硬幣不夠……',
      bought: '☂️ 買到雨傘了！下雨也擋不住你。',
      have: '你的包包裡已經有一把傘了。',
    },

    calls: {
      momTyphoon: {
        caller: '媽媽 ❤️',
        desc: '擔心颱風',
        intro: '寶貝，颱風快來了！你在哪裡？風變大之前趕快回家，也不要靠近河邊。',
        good: '「我現在就回家，媽媽。我來幫你準備！」',
        goodMsg: '媽媽聽起來鬆了一口氣：「謝謝你，寶貝。小心喔！」',
        bad: '「只是一點風而已，我要待在外面。」',
        badMsg: '媽媽的聲音因為擔心而緊繃起來……',
      },
      momQuake: {
        caller: '媽媽 ❤️',
        desc: '地震過後馬上打來',
        intro: '你剛剛有感覺到地震嗎？有沒有受傷？你現在在哪裡？',
        good: '「我沒事，媽媽！我有趴下、掩護、穩住。」',
        goodMsg: '媽媽終於鬆了口氣：「很好！就是要這樣做。」',
        bad: '「唉唷，媽，又沒什麼。」',
        badMsg: '媽媽小聲地說：「我只是很擔心你……」',
      },
      momRain: {
        caller: '媽媽 ❤️',
        desc: '下雨了',
        intro: '外面下雨了！你有帶傘嗎？不要淋濕感冒了。',
        good: '「謝謝媽媽！我去超商買把傘。」',
        goodMsg: '媽媽：「乖孩子。回家記得把頭髮吹乾！」',
        bad: '「下雨沒差啦，我不在乎。」',
        badMsg: '媽媽嘆氣：「那就別打著噴嚏回家喔……」',
      },
    },

    journal: {
      typhoonReady: '颱風來之前把家裡準備好了',
      quakeSafe: '地震時保護好自己：趴下、掩護、穩住！',
    },
  },
};
